<template>
    <div class="rf-view notification-center-view shade">
        <NavigationBar :title="$t('notifications.center.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
            <button v-if="!loading && notifications.length > 0" slot="right" class="button text" type="button" @click="clearAll">
                {{ $t('notifications.center.clearAll') }}
            </button>
        </NavigationBar>
        <main>
            <h1>
                {{ $t('notifications.center.title') }}
            </h1>

            <Spinner v-if="loading" />

            <div v-else-if="notifications.length === 0" class="notifications-empty">
                <span class="icon-circle"><span class="icon bell" /></span>
                <p class="style-description">
                    {{ $t('notifications.center.empty') }}
                </p>
            </div>

            <transition-group v-else tag="div" name="notification-swipe" class="notification-list">
                <div
                    v-for="(n, index) in notifications"
                    :key="n.id"
                    class="notification-row"
                    :style="{ '--index': Math.min(index, 8) }"
                    @touchstart="onTouchStart(n, $event)"
                    @touchmove="onTouchMove($event)"
                    @touchend="onTouchEnd(n, $event)"
                    @touchcancel="onTouchCancel()"
                >
                    <component
                        :is="n.promotionId ? 'button' : 'article'"
                        class="notification-card"
                        :class="{ unread: !n.readAt, selectable: !!n.promotionId }"
                        :style="swipeStyle(n.id)"
                        @click="openPromotion(n)"
                    >
                        <span class="icon-circle">
                            <span class="icon" :class="iconFor(n.type)" />
                        </span>
                        <div class="content">
                            <div class="first-line">
                                <p class="title">
                                    {{ n.title }}
                                </p>
                                <time class="date" :datetime="n.createdAt.toISOString()">{{ formatDate(n.createdAt) }}</time>
                            </div>
                            <p class="body">
                                {{ n.body }}
                            </p>
                        </div>
                        <span v-if="n.promotionId" class="icon arrow-right chevron" />
                    </component>
                    <button class="dismiss-button icon close" type="button" :aria-label="$t('notifications.center.dismiss')" @click.stop="dismissNotification(n)" />
                </div>
            </transition-group>
        </main>
    </div>
</template>

<script lang="ts">
import { BackButton, NavigationBar, Spinner } from "@restofrit/components";
import { Session } from "@restofrit/networking";
import { Customer, CustomerNotificationResponse, CustomerNotificationsListResponse, CustomerNotificationType, PromotionCustomer, UnreadCountResponse } from "@restofrit/structures";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { ComponentWithProperties, NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop } from "vue-property-decorator";

import { clearOSBadge, markNotificationsRead } from "../../helpers/NotificationBadge";
import PromotionView from "../promotions/PromotionView.vue";

@Component({
    components: {
        NavigationBar,
        BackButton,
        Spinner
    }
})
export default class NotificationCenterView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer!: Customer

    loading = true
    notifications: CustomerNotificationResponse[] = []
    promotionsCache: PromotionCustomer[] | null = null

    // Swipe-to-dismiss state
    swipingId: string | null = null
    swipeDx = 0
    swipeSettling = false
    private swipeAxis: "x" | "y" | null = null
    private touchStartX = 0
    private touchStartY = 0
    private touchStartTime = 0
    private suppressClick = false

    async mounted() {
        await this.load()
        // Mark all notifications as read and clear the OS badge
        markNotificationsRead(this.customer.id).catch(console.error)
        clearOSBadge().catch(console.error)
    }

    async load() {
        this.loading = true
        try {
            const response = await Session.shared.authenticatedServer.request({
                method: "GET",
                path: "/customers/" + this.customer.id + "/notifications",
                decoder: CustomerNotificationsListResponse as Decoder<CustomerNotificationsListResponse>
            })
            this.notifications = response.data.notifications
        } catch (e) {
            console.error("Failed to load notifications", e)
        }
        this.loading = false
    }

    iconFor(type: CustomerNotificationType): string {
        switch (type) {
            case CustomerNotificationType.Reminder: return "bell"
            case CustomerNotificationType.Account: return "settings"
            default: return "promotions"
        }
    }

    formatDate(date: Date): string {
        const locale = this.customer.settings.language
        if (date.toDateString() === new Date().toDateString()) {
            // Today: show the time instead of the date
            return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)
        }
        return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date)
    }

    async fetchPromotions(): Promise<PromotionCustomer[]> {
        if (this.promotionsCache) {
            return this.promotionsCache
        }
        const response = await Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/customers/" + this.customer.id + "/promotions",
            decoder: new ArrayDecoder(PromotionCustomer as Decoder<PromotionCustomer>)
        })
        this.promotionsCache = response.data
        return response.data
    }

    swipeStyle(id: string): Record<string, string> {
        if (this.swipingId !== id) {
            return {}
        }
        return {
            transform: `translateX(${this.swipeDx}px)`,
            opacity: String(Math.max(0.3, 1 - Math.abs(this.swipeDx) / 300)),
            transition: this.swipeSettling ? "transform 0.25s ease, opacity 0.25s ease" : "none"
        }
    }

    onTouchStart(notification: CustomerNotificationResponse, event: TouchEvent) {
        if (event.touches.length !== 1) {
            return
        }
        this.swipingId = notification.id
        this.swipeDx = 0
        this.swipeSettling = false
        this.swipeAxis = null
        this.touchStartX = event.touches[0].clientX
        this.touchStartY = event.touches[0].clientY
        this.touchStartTime = Date.now()
    }

    onTouchMove(event: TouchEvent) {
        if (this.swipingId === null || event.touches.length !== 1) {
            return
        }
        const dx = event.touches[0].clientX - this.touchStartX
        const dy = event.touches[0].clientY - this.touchStartY
        if (this.swipeAxis === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
            this.swipeAxis = Math.abs(dx) > Math.abs(dy) ? "x" : "y"
        }
        if (this.swipeAxis !== "x") {
            return
        }
        // Left swipes only; give right drags heavy resistance
        this.swipeDx = dx < 0 ? dx : dx / 8
    }

    onTouchEnd(notification: CustomerNotificationResponse, event: TouchEvent) {
        if (this.swipingId !== notification.id) {
            return
        }
        if (this.swipeAxis === "x") {
            // A horizontal swipe must never count as a tap on the card
            this.suppressClick = true
            setTimeout(() => { this.suppressClick = false }, 100)

            const width = (event.currentTarget as HTMLElement).offsetWidth
            const elapsed = Date.now() - this.touchStartTime
            const flick = this.swipeDx < -30 && elapsed < 250
            if (this.swipeDx < -width * 0.4 || flick) {
                this.swipingId = null
                this.dismissNotification(notification).catch(console.error)
                return
            }
        }
        // Spring back
        this.swipeSettling = true
        this.swipeDx = 0
        setTimeout(() => {
            if (this.swipeDx === 0) {
                this.swipingId = null
            }
        }, 250)
    }

    onTouchCancel() {
        this.swipeSettling = true
        this.swipeDx = 0
        this.swipingId = null
    }

    async dismissNotification(notification: CustomerNotificationResponse) {
        const index = this.notifications.indexOf(notification)
        if (index === -1) {
            return
        }
        this.notifications.splice(index, 1)
        try {
            await Session.shared.authenticatedServer.request({
                method: "DELETE",
                path: "/customers/" + this.customer.id + "/notifications/" + notification.id,
                decoder: UnreadCountResponse as Decoder<UnreadCountResponse>
            })
        } catch (e) {
            console.error("Failed to dismiss notification", e)
            this.notifications.splice(Math.min(index, this.notifications.length), 0, notification)
        }
    }

    async clearAll() {
        if (!confirm(this.$t('notifications.center.clearAllConfirm') as string)) {
            return
        }
        const previous = this.notifications
        this.notifications = []
        try {
            await Session.shared.authenticatedServer.request({
                method: "DELETE",
                path: "/customers/" + this.customer.id + "/notifications",
                decoder: UnreadCountResponse as Decoder<UnreadCountResponse>
            })
        } catch (e) {
            console.error("Failed to clear notifications", e)
            this.notifications = previous
        }
    }

    async openPromotion(notification: CustomerNotificationResponse) {
        if (this.suppressClick) {
            return
        }
        if (!notification.promotionId) {
            return
        }
        try {
            const promotions = await this.fetchPromotions()
            const promotion = promotions.find(p => p.id === notification.promotionId)
            if (promotion) {
                this.show(new ComponentWithProperties(PromotionView, { promotion, customer: this.customer }))
            } else {
                window.alert(this.$t('notifications.promotionNotFound'))
            }
        } catch (e) {
            console.error("Failed to load promotion", e)
        }
    }
}
</script>

<style lang="scss">
@use '~@restofrit/scss/base/variables' as *;

.notification-center-view {
    .notification-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 500px;
    }

    .notification-row {
        position: relative;
        touch-action: pan-y;
        animation: notification-card-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        animation-delay: calc(var(--index, 0) * 50ms);
    }

    .dismiss-button {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 28px;
        height: 28px;
        padding: 0;
        margin: 0;
        border: 0;
        background: none;
        cursor: pointer;
        color: $color-gray-light;
        font-size: 14px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:active {
            color: $color-gray;
        }
    }

    // transition-group: dismissed card slides out left, siblings close the gap
    .notification-swipe-leave-active {
        transition: transform 0.25s ease, opacity 0.25s ease;
        position: absolute;
        width: 100%;
    }

    .notification-swipe-leave-to {
        transform: translateX(-110%);
        opacity: 0;
    }

    .notification-swipe-move {
        transition: transform 0.25s ease;
    }

    .icon-circle {
        flex-shrink: 0;
        position: relative;
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background: $color-background-shade;
        color: $color-gray;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon {
            width: 24px;
            font-size: 24px;
        }
    }

    .notification-card {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 14px;
        text-align: left;
        width: 100%;
        box-sizing: border-box;
        padding: 16px;
        margin: 0;
        border: 0;
        font: inherit;
        color: inherit;
        background: $color-background;
        border-radius: 15px;
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);

        > .content {
            flex-grow: 1;
            min-width: 0;

            > .first-line {
                display: flex;
                flex-direction: row;
                align-items: baseline;
                gap: 10px;

                > .title {
                    flex-grow: 1;
                    font-family: Poppins;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 140%;
                    margin: 0;
                    color: $color-dark;
                }

                > .date {
                    flex-shrink: 0;
                    font-family: Poppins;
                    font-size: 12px;
                    line-height: 140%;
                    color: $color-gray;
                    white-space: nowrap;
                    // Keep clear of the absolutely positioned dismiss button
                    padding-right: 24px;
                }
            }

            > .body {
                font-family: Poppins;
                font-weight: normal;
                font-size: 14px;
                line-height: 140%;
                margin: 3px 0 0;
                color: $color-gray;
                white-space: normal;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
        }

        > .chevron {
            flex-shrink: 0;
            width: 20px;
            font-size: 20px;
            color: $color-gray-light;
        }

        &.unread {
            .icon-circle {
                background: $color-background-primary;
                color: $color-primary;

                // Badge dot, mirroring the bell badge on the promotions view
                &:after {
                    content: '';
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: $color-primary;
                    box-shadow: 0 0 0 2px $color-background;
                }
            }
        }

        &.selectable {
            cursor: pointer;
            touch-action: manipulation;
            user-select: none;
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            -webkit-touch-callout: none;
            transition: transform 0.2s 0.15s;

            &:active {
                transform: scale(0.97);
                transition: transform 0.1s;
            }
        }
    }

    .notifications-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
        text-align: center;
        padding: 50px 20px;

        .icon-circle {
            width: 64px;
            height: 64px;
            background: $color-background-primary;
            color: $color-primary;

            .icon {
                width: 30px;
                font-size: 30px;
            }
        }
    }

    @keyframes notification-card-in {
        from {
            opacity: 0;
            transform: translateY(12px);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .notification-row {
            animation: none;
        }
        .notification-swipe-leave-active,
        .notification-swipe-move {
            transition: none;
        }
    }
}
</style>
